import type { SimEventType } from '@/events/simEvents';

export type DemoGroup =
  | 'inbound'
  | 'floor'
  | 'money-signals'
  | 'leaderboard-reporting';

export interface DemoCard {
  id: string;
  group: DemoGroup;
  /** Action-oriented title — reads like a real-world prompt. */
  title: string;
  /** One-line explanation of what the facilitator should watch for. */
  subtitle: string;
  /** Emoji shown on the card. Keeps the catalogue scannable. */
  emoji: string;
  /** Either fire a scripted sim event, or just navigate, or both. */
  trigger:
    | { kind: 'sim-event'; eventType: SimEventType }
    | { kind: 'navigate-only' };
  /** Where to land the facilitator after the trigger fires. */
  gotoPath?: string;
  /** Optional location state passed through React Router. */
  gotoState?: Record<string, unknown>;
}

/**
 * Demo console catalogue. Each entry is a one-click facilitator action that
 * fires a real sim event AND/OR navigates to the surface where the demo plays
 * out, so the audience sees the outcome immediately.
 *
 * Grouped by workflow rather than time. The TopBar "Next event" button still
 * exists for anyone who wants the chronological Monday walk-through.
 */
export const DEMO_CATALOGUE: DemoCard[] = [
  // ── Inbound: what comes IN
  {
    id: 'demo-sarah-missed-call',
    group: 'inbound',
    title: 'Sarah calls overnight (voicemail)',
    subtitle: 'Sarah Holman left a 3:14am voicemail. Lands in the queue with a transcript.',
    emoji: '📨',
    trigger: { kind: 'sim-event', eventType: 'sarah-missed-call' },
    gotoPath: '/queue',
  },
  {
    id: 'demo-multi-studio-inbound',
    group: 'inbound',
    title: 'Live call ringing — answer it',
    subtitle: 'Inbound to Frankston. Cross-studio history visible. Screen-pop on answer.',
    emoji: '📞',
    trigger: { kind: 'sim-event', eventType: 'multi-studio-inbound' },
    gotoPath: '/queue',
  },
  {
    id: 'demo-walk-in',
    group: 'inbound',
    title: 'Walk-in at Coburg',
    subtitle: 'Apprentice captures a fresh walk-in. Lands on the floor.',
    emoji: '🚶',
    trigger: { kind: 'sim-event', eventType: 'walk-in-arrives' },
    gotoPath: '/floor',
  },

  // ── Floor + checkout — exercising the new wizard
  {
    id: 'demo-floor-aaron',
    group: 'floor',
    title: 'Aaron is in chair right now',
    subtitle: 'Live timer running. Ambient notes + snaps panels. Wrap up to start checkout.',
    emoji: '🔴',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/floor/bk-floor-in-chair',
  },
  {
    id: 'demo-outcome-a',
    group: 'floor',
    title: 'Outcome A — lock the next session',
    subtitle: "Wrap Aaron up. Pick Outcome A. Deposit rolls forward, leaderboard ticks Pat.",
    emoji: '🪑',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/floor/bk-floor-in-chair',
  },
  {
    id: 'demo-outcome-b',
    group: 'floor',
    title: 'Outcome B — they decline, apply the deposit',
    subtitle: 'Reason chip is required. Attribution selector hidden — nothing to credit.',
    emoji: '🚪',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/floor/bk-floor-in-chair',
  },
  {
    id: 'demo-outcome-c',
    group: 'floor',
    title: 'Outcome C — defer to sales',
    subtitle: 'Sales follow-up task created. Lands in the bookings agent queue with resolve buttons.',
    emoji: '📞',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/floor/bk-floor-in-chair',
  },
  {
    id: 'demo-pat-session-finish',
    group: 'floor',
    title: 'Pat finishes a session',
    subtitle: "Increments today's in-chair rebookings tally and drops a comms ping.",
    emoji: '✅',
    trigger: { kind: 'sim-event', eventType: 'pat-session-finish' },
    gotoPath: '/floor',
  },

  // ── Money + Stripe signals
  {
    id: 'demo-sarah-deposit',
    group: 'money-signals',
    title: "Sarah's deposit just landed",
    subtitle: 'Stripe webhook fires. Booking created. Coburg + Pat, Saturday 11am.',
    emoji: '💳',
    trigger: { kind: 'sim-event', eventType: 'sarah-stripe-deposit' },
    gotoPath: '/deposits',
  },
  {
    id: 'demo-deposit-expires',
    group: 'money-signals',
    title: 'A deposit is about to expire',
    subtitle: 'Flags an existing pending deposit with 45 min left. Queue urgency pops.',
    emoji: '⏳',
    trigger: { kind: 'sim-event', eventType: 'deposit-expires-warning' },
    gotoPath: '/queue',
  },
  {
    id: 'demo-confirmations',
    group: 'money-signals',
    title: "Tomorrow's confirmation SMS queue",
    subtitle: '23 sequences ready. Each one is a 4-message thread — confirm + getting here + prep + closer.',
    emoji: '📲',
    trigger: { kind: 'sim-event', eventType: 'confirmations-queue-4pm' },
    gotoPath: '/confirmations',
  },
  {
    id: 'demo-daniel-future',
    group: 'money-signals',
    title: "Daniel's May trigger fires",
    subtitle: 'Future-readiness signal six months on. A draft re-contact waits for approval.',
    emoji: '⏰',
    trigger: { kind: 'sim-event', eventType: 'daniel-future-readiness-fires' },
    gotoPath: '/future-readiness',
  },
  {
    id: 'demo-links-resend-free',
    group: 'money-signals',
    title: 'Expired link — date still free',
    subtitle: "Resend Priya's lapsed deposit link. Original slot is open, so a fresh link goes out for the same date.",
    emoji: '🔗',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/deposits',
  },
  {
    id: 'demo-links-resend-taken',
    group: 'money-signals',
    title: 'Expired link — date taken',
    subtitle: "Resend Dana's lapsed link. The slot is booked now, so she jumps to the TOP of the Money queue to rebook (check /queue).",
    emoji: '⛔',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/deposits',
  },
  {
    id: 'demo-links-waiver-t7',
    group: 'money-signals',
    title: 'Waiver fires at T-4 days',
    subtitle: "Theo's session is 5 days out. Advance the clock one day (top bar) — the waiver auto-sends as it crosses 4 days; watch the tick.",
    emoji: '📝',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/confirmations',
  },
  {
    id: 'demo-links-ics-invite',
    group: 'money-signals',
    title: 'Calendar invite on the confirmation',
    subtitle: "Mia's confirmation carries a downloadable .ics; a reschedule bumped it to SEQUENCE 1 on the same event.",
    emoji: '📅',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/customer/cust-links-ics',
  },

  // ── Leaderboard, reporting, EOD
  {
    id: 'demo-brandon-rebook',
    group: 'leaderboard-reporting',
    title: 'Brandon lands an in-chair rebook',
    subtitle: "Watch the leaderboard — Brandon ticks +1, movement arrow updates on the next row read.",
    emoji: '🏆',
    trigger: { kind: 'sim-event', eventType: 'brandon-rebook-lands' },
    gotoPath: '/leaderboard',
  },
  {
    id: 'demo-tyler-sales-resolve',
    group: 'leaderboard-reporting',
    title: 'Sales closes Tyler (deferred rebook)',
    subtitle: 'Resolves the seeded Outcome C task as booked. Customer record + reporting both reflect.',
    emoji: '✅',
    trigger: { kind: 'sim-event', eventType: 'tyler-sales-resolves' },
    gotoPath: '/reporting',
  },
  {
    id: 'demo-reporting-attribution',
    group: 'leaderboard-reporting',
    title: 'Show the attribution dashboard',
    subtitle: 'Per-studio outcome split, top facilitators, sales conversion rate.',
    emoji: '📈',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/reporting',
  },
  {
    id: 'demo-leaderboard-tv',
    group: 'leaderboard-reporting',
    title: 'Studio wall display (Coburg)',
    subtitle: 'Fullscreen TV mode. Top 3 only. Big type. Movement arrows visible across the room.',
    emoji: '📺',
    trigger: { kind: 'navigate-only' },
    gotoPath: '/leaderboard/tv/coburg',
  },
  {
    id: 'demo-eod-metrics',
    group: 'leaderboard-reporting',
    title: 'End of day metrics roll up',
    subtitle: '28 deposits at Coburg. 4 cross-studio routes. 6 missed calls recovered.',
    emoji: '🌙',
    trigger: { kind: 'sim-event', eventType: 'eod-metrics' },
    gotoPath: '/owner',
  },
];

export const GROUP_META: Record<DemoGroup, { label: string; story: string }> = {
  inbound: {
    label: "What's coming in",
    story: 'Calls, messages, walk-ins. Click any of these to see how the network catches it.',
  },
  floor: {
    label: 'In the chair + checkout',
    story: 'The floor flow — live timer, ambient notes/snaps, the new rebook outcomes wizard.',
  },
  'money-signals': {
    label: 'Money + signals',
    story: 'Deposits, hand-offs, nurture, future-readiness, tomorrow\'s confirmations.',
  },
  'leaderboard-reporting': {
    label: 'Leaderboard + reporting',
    story: 'Where the rebooks land visually. Studio walls, attribution dashboard, EOD.',
  },
};

export const GROUP_ORDER: DemoGroup[] = [
  'inbound',
  'floor',
  'money-signals',
  'leaderboard-reporting',
];
